<template lang="pug">
el-form(ref="briefForm" @submit.native.prevent='' label-position="top" :rules="briefRules" :model="brief")
  el-row(:gutter="40")
    el-col.margin(:lg="15")
      el-form-item(label="Title" prop="title")
        el-input(v-model.trim="brief.title" size="large")

      el-form-item(label="Content")
        quill-editor.quill-container(v-model="brief.content" :options='quillOptions')

      el-upload.uploader(:multiple='false' :show-file-list='false' drag action="none" :on-preview='onFilePreview' :http-request='handleFileUpload' :on-success='onUploadSuccess' :on-remove='onRemoveFile')
        i.el-icon-upload
        .el-upload__text Drop file here or click to upload
        el-progress(v-if='fileUpload.percentage' :percentage="fileUpload.percentage")
      ul.el-upload-list.el-upload-list--text(v-if="fileUpload.filePath")
        li.el-upload-list__item.is-success
          a.el-upload-list__item-name(:href='fileUpload.fileUrl' target='_blank')
            i.el-icon-document
            | {{ fileUpload.filePath }}

          label.el-upload-list__item-status-label
            i.el-icon-upload-success.el-icon-circle-check
          i.el-icon-close(@click='onRemoveFile')

      h3.questions-title Question Bank

      .question-editor-panel
        h3.questions-heading How many questions would you like to ask?
        p.question-subheading The questions will be selected at random from the list below. You can add new questions, drag and drop to arrange the order and click to edit.
        el-form-item.num-of-questions
          el-input-number(v-model="numQuestionsToAsk" @change='updateBriefModel')

        h3.questions-heading Add questions from a previous brief
        el-form-item
          el-row(:gutter="20")
            el-col(:lg='19')
              el-select(v-model="importFromBrief.selected" clearable filterable placeholder="Select" :loading='importFromBrief.loading'
                        @visible-change.once='onImportFromBriefToggle')
                el-option(v-for='brief in importFromBriefOptions' :key='brief.id' :value='brief.id' :label='brief.title' :disabled='!brief.questions.length')
                  el-row
                    el-col(:span='18') {{ brief.title }}
                    el-col(:span='6')
                      el-tag(type="primary") {{ brief.questions.length }} questions

            el-col(:lg='5')
              el-button(type='primary' :disabled='!importFromBrief.selected' @click='importQuestions') Import

        brief-question-editor(:questions='brief.questions')

    el-col(:lg="9")
      scheduler(v-if='brief' v-model='brief' @save='onSave' :form-submitted='formSubmitted' :show-delete='!create'
                @delete='onDelete' ref='scheduler')
        template(slot='middle')
          .side-title Who would you like to see this brief?
          assignee-picker(:assignees.sync='assignees')

        template(slot='saveOptions')
          slot(name='saveOptions')

</template>

<script>
import _ from "lodash";
import { mapActions } from "vuex";
import { quillEditor } from "vue-quill-editor";

import BriefQuestionEditor from "@/brapp/components/sections/briefs/BriefQuestionEditor/index";
import AssigneePicker from "@/brapp/components/shared/AssigneePicker";
import Scheduler from "@/brapp/components/shared/Scheduler";

export default {
  components: { quillEditor, BriefQuestionEditor, AssigneePicker, Scheduler },
  model: {
    prop: "brief",
    event: "change"
  },
  props: {
    createAnother: {
      type: Boolean,
      default: false
    },
    create: {
      type: Boolean,
      default: true
    },
    brief: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    brief(brief) {
      this.formSubmitted = false;
      if (brief.attachmentPath) {
        this.fileUpload = {
          percentage: 0,
          fileUrl: brief.attachmentUrl,
          filePath: brief.attachmentPath
        };
      }
    }
  },
  data() {
    return {
      loading: false,
      fileUpload: {
        percentage: 0,
        filePath: null,
        fileUrl: null
      },
      numQuestionsToAsk: 0,
      formSubmitted: false,
      importFromBrief: {
        loading: false,
        selected: null,
        options: []
      },
      briefRules: {
        title: [{ required: true, message: "Required field", trigger: "blur" }]
      },
      quillOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"]
          ]
        }
      }
    };
  },
  computed: {
    assignees: {
      get() {
        return {
          users: this.brief.assignees.filter(a => a.user).map(a => a.user),
          appLicenseGroups: this.brief.assignees
            .filter(a => a.appLicenseGroup)
            .map(a => a.appLicenseGroup)
        };
      },
      set(val) {
        this.brief.assignees = val.users
          .map(u => ({ user: u }))
          .concat(val.appLicenseGroups.map(g => ({ appLicenseGroup: g })));
      }
    },
    importFromBriefOptions() {
      return this.importFromBrief.options.filter(
        brief => brief.id !== this.brief.id
      );
    }
  },
  methods: {
    ...mapActions({
      uploadFile: "upload/uploadFile",
      fetchBriefs: "briefs/fetchBriefs"
    }),
    onImportFromBriefToggle(dropdownOpen) {
      if (dropdownOpen) {
        this.importFromBrief.loading = true;
        this.fetchBriefs().then(data => {
          this.importFromBrief.options = data;
          this.importFromBrief.loading = false;
        });
      }
    },
    importQuestions() {
      // Find selected brief object
      const selectedBrief = this.importFromBrief.options.find(
        b => b.id === this.importFromBrief.selected
      );
      // Make sure we remove the ids from the existing questions/answers so that copies are created when we send them
      // to the api
      const newQuestions = selectedBrief.questions.map(q => ({
        ..._.omit(q, "id"),
        answers: q.answers.map(a => _.omit(a, "id"))
      }));
      this.brief.questions.push(...newQuestions);
      // Clear selected option
      this.importFromBrief.selected = null;
      // Emit model change
      this.$emit("change", this.brief);
    },
    updateBriefModel(val) {
      this.brief.numQuestionsToAsk = val;
      this.$emit("change", this.brief);
    },
    selectFile(event) {
      this.attachment = event.target.files[0];
    },
    clearFile() {
      this.attachment = null;
    },
    handleFileUpload({ file }) {
      return this.uploadFile({
        file,
        bucketKey: "briefs",
        options: { onProgress: this.onUploadProgress }
      });
    },
    onUploadProgress(percentage) {
      this.fileUpload = {
        percentage
      };
    },
    onFilePreview({ url }) {
      window.open(url);
    },
    onRemoveFile() {
      this.fileUpload = {
        percentage: 0
      };
      this.brief.attachmentPath = null;
    },
    onUploadSuccess(resp) {
      this.brief.attachmentPath = resp.path;
      this.fileUpload = {
        percentage: 0,
        filePath: resp.path,
        fileUrl: resp.fullUrl
      };
    },
    onSave() {
      this.formSubmitted = true;
      this.$refs.briefForm.validate(valid => {
        if (this.$refs.scheduler.datesValid) {
          this.$emit("save", { valid });
        } else {
          this.$emit("save", { valid: false });
        }
      });
    },
    onDelete() {
      this.$emit("delete");
    }
  },
  created() {
    this.numQuestionsToAsk = this.brief.numQuestionsToAsk;
    if (this.brief.attachmentPath) {
      this.fileUpload = {
        filePath: this.brief.attachmentPath,
        fileUrl: this.brief.attachmentUrl
      };
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../../../../assets/scss/mixins.scss";
.side-title {
  font-family: "gilroyextrabold";
  text-transform: uppercase;
  font-weight: normal;
  letter-spacing: 0.05em;
  font-size: 14px;
  color: #2c3e50;
  margin: 10px 0 5px 0;
}

.quill-editor {
  background: white;
}

.filter-users-wrap {
  margin-bottom: 15px;
}

.quill-container {
  :global(.ql-container) {
    background: white;
    height: 200px;
  }
}
.questions-title {
  letter-spacing: 0.05em;
  font-size: 28px;
  font-family: "gilroylight";
  font-weight: normal;
  margin: 40px 0 20px 0;
}
.questions-heading {
  font-family: "gilroyextrabold";
  text-transform: uppercase;
  font-weight: normal;
  letter-spacing: 0.05em;
  font-size: 14px;
  margin-bottom: 5px;
  color: #2c3e50;
}

.question-subheading {
  font-size: 13px;
  margin-bottom: 10px;
}

.question-editor-panel {
  background: white;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 2px;
  height: auto;
  display: block;
}

.uploader {
  :global(.el-upload) {
    display: block;
  }
  :global(.el-upload-dragger) {
    width: 100%;
    border: 1px solid #ddd;
    padding: 0 40px;
    border-radius: 2px;
  }
}
@include MQ(ML) {
  .margin {
    margin-bottom: 20px;
  }
}
</style>
